
import { useEffect, useRef, useState } from "react";
import { useMarketingLang } from "../i18n.jsx";

type OldRowState = "hidden" | "checking" | "fail";
type PillState = "go" | "warn" | "fail";

const OLD_SITES: { name: string; reason: string; weeks: number }[] = [
  { name: "Mongstad parcel 47/12", reason: "grid queue · 2034", weeks: 11 },
  { name: "Karmøy parcel 89/3", reason: "LNF zoning", weeks: 8 },
  { name: "Tjeldbergodden 12/8", reason: "salmon river buffer", weeks: 14 },
  { name: "Stavanger 204/15", reason: "noise buffer · airport", weeks: 9 },
];

const TOTAL_PARCELS = 17127;
const VIABLE = 12;

// 6×5 = 30 pills, mostly go with a few warn/fail to feel real
const NEW_PILLS: PillState[] = [
  "go",   "warn", "go",   "go",   "go",   "warn",
  "go",   "go",   "fail", "go",   "warn", "go",
  "go",   "warn", "go",   "go",   "go",   "go",
  "warn", "go",   "go",   "fail", "go",   "go",
  "go",   "go",   "warn", "go",   "go",   "go",
];

const PILL_FILL_END_MS = 260 + (NEW_PILLS.length - 1) * 22 + 380; // last pill done
const PARCEL_TICK_END_MS = 800 + 1500;
const OLD_DONE_MS = 350 + (OLD_SITES.length - 1) * 950 + 750;
const VIABLE_START_MS = Math.max(PILL_FILL_END_MS, PARCEL_TICK_END_MS) + 200;

export default function HeroShift() {
  const { t } = useMarketingLang();
  const [oldStates, setOldStates] = useState<OldRowState[]>(
    OLD_SITES.map(() => "hidden")
  );
  const [pillsActive, setPillsActive] = useState(false);
  const [parcelCount, setParcelCount] = useState(0);
  const [viableCount, setViableCount] = useState(0);
  const [newDone, setNewDone] = useState(false);
  const [oldElapsed, setOldElapsed] = useState(0);
  const [newElapsed, setNewElapsed] = useState(0);
  const oldElapsedRaf = useRef<number | null>(null);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    // ─── OLD side: sequential checking → fail
    OLD_SITES.forEach((_, i) => {
      timers.push(
        setTimeout(() => {
          setOldStates((prev) =>
            prev.map((s, idx) => (idx === i ? "checking" : s))
          );
        }, 350 + i * 950)
      );
      timers.push(
        setTimeout(() => {
          setOldStates((prev) =>
            prev.map((s, idx) => (idx === i ? "fail" : s))
          );
        }, 350 + i * 950 + 750)
      );
    });

    // ─── NEW side: pills pop, counter ticks
    timers.push(setTimeout(() => setPillsActive(true), 260));

    const parcelStart = performance.now() + 800;
    const parcelDur = 1500;
    let parcelRaf = 0;
    const parcelTick = (now: number) => {
      if (now < parcelStart) {
        parcelRaf = requestAnimationFrame(parcelTick);
        return;
      }
      const p = Math.min(1, (now - parcelStart) / parcelDur);
      const eased = 1 - Math.pow(1 - p, 3);
      setParcelCount(Math.round(TOTAL_PARCELS * eased));
      if (p < 1) parcelRaf = requestAnimationFrame(parcelTick);
    };
    parcelRaf = requestAnimationFrame(parcelTick);

    // Lock NEW elapsed at the moment the parallel run "finishes"
    const newFinishMs = Math.max(PILL_FILL_END_MS, PARCEL_TICK_END_MS);
    timers.push(
      setTimeout(() => {
        setNewElapsed(newFinishMs / 1000);
        setNewDone(true);
      }, newFinishMs)
    );

    // Viable counter pulses in after both sides have settled enough
    timers.push(
      setTimeout(() => {
        const vStart = performance.now();
        const vDur = 700;
        const vTick = (now: number) => {
          const p = Math.min(1, (now - vStart) / vDur);
          const eased = 1 - Math.pow(1 - p, 3);
          setViableCount(Math.round(VIABLE * eased));
          if (p < 1) requestAnimationFrame(vTick);
        };
        requestAnimationFrame(vTick);
      }, VIABLE_START_MS)
    );

    // Elapsed counter on OLD side runs the whole time, locks when OLD finishes
    const oldStart = performance.now();
    const oldTickFn = (now: number) => {
      const e = Math.min(OLD_DONE_MS / 1000, (now - oldStart) / 1000);
      setOldElapsed(e);
      if (e * 1000 < OLD_DONE_MS) {
        oldElapsedRaf.current = requestAnimationFrame(oldTickFn);
      }
    };
    oldElapsedRaf.current = requestAnimationFrame(oldTickFn);

    return () => {
      timers.forEach(clearTimeout);
      cancelAnimationFrame(parcelRaf);
      if (oldElapsedRaf.current !== null) {
        cancelAnimationFrame(oldElapsedRaf.current);
      }
    };
  }, []);

  return (
    <div className="relative">
      <div className="rounded-2xl overflow-hidden border-hairline-base bg-bg-card shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]">
        {/* Console header */}
        <div className="flex items-center justify-between px-5 py-3 border-b-[0.5px] border-white/[0.08] bg-[rgba(8,12,20,0.6)]">
          <div className="flex items-center gap-3">
            <span className="pulse-dot inline-block w-2 h-2 rounded-full bg-teal" />
            <span className="font-mono text-[10.5px] uppercase tracking-[0.21em] text-white/65">
              {t('heroShift.consoleHeader')}
            </span>
          </div>
          <span className="font-mono text-[10.5px] uppercase tracking-[0.21em] text-white/35">
            v0.4
          </span>
        </div>

        {/* Body */}
        <div className="grid md:grid-cols-2">
          {/* OLD column */}
          <div
            className="relative p-6 md:p-8 flex flex-col border-b-[0.5px] md:border-b-0 md:border-r-[0.5px] border-white/[0.06]"
            style={{
              background:
                "linear-gradient(180deg, rgba(226,75,74,0.045), transparent 70%)",
            }}
          >
            <div className="flex items-center justify-between mb-5">
              <p
                className="font-mono text-[10.5px] uppercase"
                style={{ letterSpacing: "0.21em", color: "#e24b4a" }}
              >
                {t('heroShift.oldLabel')}
              </p>
              <span className="font-mono text-[11px] tabular-nums text-white/45">
                {oldElapsed.toFixed(1)}s
              </span>
            </div>

            <h3 className="text-[16px] font-medium text-ink/95 mb-1">
              {t('heroShift.oldTitle')}
            </h3>
            <p className="text-[12.5px] text-white/55 leading-[1.55] mb-5 max-w-[340px]">
              {t('heroShift.oldDesc')}
            </p>

            <div className="flex flex-col gap-2 min-h-[200px]">
              {OLD_SITES.map((site, i) => {
                const state = oldStates[i];
                const hidden = state === "hidden";
                const checking = state === "checking";
                const fail = state === "fail";
                return (
                  <div
                    key={site.name}
                    className="flex items-center gap-3 px-3.5 py-2.5 rounded-lg border-hairline-base"
                    style={{
                      background: fail
                        ? "rgba(226,75,74,0.10)"
                        : checking
                        ? "rgba(240,178,58,0.07)"
                        : "rgba(240,178,58,0.05)",
                      borderColor: fail
                        ? "rgba(226,75,74,0.28)"
                        : "rgba(240,178,58,0.20)",
                      opacity: hidden ? 0 : 1,
                      transform: hidden ? "translateX(-12px)" : "translateX(0)",
                      transition:
                        "background 380ms ease-out, border-color 380ms ease-out, opacity 320ms ease-out, transform 320ms ease-out",
                    }}
                  >
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${
                        checking ? "pulse-dot" : ""
                      }`}
                      style={{
                        background: fail ? "#e24b4a" : "#f0b23a",
                        transition: "background 380ms ease-out",
                      }}
                    />
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-[12.5px] text-ink/90 truncate">
                        {site.name}
                      </span>
                      <span className="font-mono text-[10px] text-white/40 truncate">
                        {checking
                          ? t('heroShift.checking')
                          : fail
                          ? site.reason
                          : ""}
                      </span>
                    </div>
                    <span
                      className="font-mono text-[10.5px] uppercase tracking-[0.16em] tabular-nums"
                      style={{ color: fail ? "#e24b4a" : "#f0b23a" }}
                    >
                      {fail ? t('heroShift.fail') : checking ? `${site.weeks}w` : ""}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="mt-auto pt-6 flex items-baseline gap-2">
              <span className="font-mono text-[22px] text-ink/85 tabular-nums leading-none">
                6–18
              </span>
              <span className="text-[12.5px] text-white/55">{t('heroShift.monthsPerCycle')}</span>
            </div>
          </div>

          {/* NEW column */}
          <div
            className="relative p-6 md:p-8 flex flex-col"
            style={{
              background:
                "linear-gradient(180deg, rgba(77,212,232,0.055), transparent 70%)",
            }}
          >
            <div className="flex items-center justify-between mb-5">
              <p
                className="font-mono text-[10.5px] uppercase"
                style={{ letterSpacing: "0.21em", color: "#4dd4e8" }}
              >
                {t('heroShift.newLabel')}
              </p>
              <span
                className="font-mono text-[11px] tabular-nums transition-colors duration-300"
                style={{ color: newDone ? "#4dd4e8" : "rgba(255,255,255,0.45)" }}
              >
                {newDone ? `${t('heroShift.done')} · ${newElapsed.toFixed(1)}s` : t('heroShift.running')}
              </span>
            </div>

            <h3 className="text-[16px] font-medium text-ink/95 mb-1">
              {t('heroShift.newTitle')}
            </h3>
            <p className="text-[12.5px] text-white/55 leading-[1.55] mb-5 max-w-[340px]">
              {t('heroShift.newDesc')}
            </p>

            <div className="flex items-baseline gap-2.5 mb-3">
              <span className="font-mono text-[24px] text-teal tabular-nums leading-none">
                {parcelCount.toLocaleString()}
              </span>
              <span className="text-[12px] text-white/55">{t('heroShift.parcelsScreened')}</span>
            </div>

            <div className="grid grid-cols-6 gap-1.5 mb-1">
              {NEW_PILLS.map((p, i) => (
                <div
                  key={i}
                  className="aspect-square rounded-[5px] border-hairline-base relative"
                  style={{
                    background: pillBg(p),
                    borderColor: pillBorder(p),
                    opacity: pillsActive ? 1 : 0,
                    transform: pillsActive ? "scale(1)" : "scale(0.5)",
                    transition:
                      "opacity 320ms ease-out, transform 380ms cubic-bezier(0.34, 1.56, 0.64, 1)",
                    transitionDelay: `${260 + i * 22}ms`,
                  }}
                >
                  <span
                    className="absolute inset-0 m-auto w-1.5 h-1.5 rounded-full"
                    style={{ background: pillDot(p) }}
                  />
                </div>
              ))}
            </div>

            <div className="mt-auto pt-6 flex items-baseline gap-2">
              <span
                className="font-mono text-[22px] text-amber tabular-nums leading-none transition-all"
                style={{
                  opacity: viableCount > 0 ? 1 : 0.3,
                }}
              >
                {viableCount}
              </span>
              <span className="text-[12.5px] text-white/55">
                {t('heroShift.viableReady')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Floating chip — only shows once new side is done */}
      <div
        className="absolute -bottom-5 left-4 md:left-10 px-4 py-3 rounded-xl bg-bg-card border-hairline-base shadow-2xl hidden sm:flex items-center gap-3 transition-all duration-500"
        style={{
          opacity: newDone ? 1 : 0,
          transform: newDone ? "translateY(0)" : "translateY(8px)",
        }}
      >
        <span className="pulse-dot inline-block w-2 h-2 rounded-full bg-teal" />
        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/65">
          {t('heroShift.live')} · {TOTAL_PARCELS.toLocaleString()} {t('heroShift.parcels')}
        </span>
      </div>

      <div
        className="absolute -top-5 right-4 md:right-10 px-4 py-3 rounded-xl bg-bg-card border-hairline-base shadow-2xl hidden sm:flex items-center gap-3 transition-all duration-500"
        style={{
          opacity: viableCount > 0 ? 1 : 0,
          transform: viableCount > 0 ? "translateY(0)" : "translateY(-8px)",
        }}
      >
        <span className="font-mono text-[20px] text-amber tabular-nums leading-none">
          {viableCount}
        </span>
        <span className="text-[12px] text-white/70 leading-tight">
          / {TOTAL_PARCELS.toLocaleString()}
          <br />
          {t('heroShift.viable')}
        </span>
      </div>
    </div>
  );
}

function pillBg(p: PillState) {
  if (p === "go") return "rgba(77,212,232,0.18)";
  if (p === "warn") return "rgba(240,178,58,0.18)";
  return "rgba(226,75,74,0.18)";
}
function pillBorder(p: PillState) {
  if (p === "go") return "rgba(77,212,232,0.42)";
  if (p === "warn") return "rgba(240,178,58,0.38)";
  return "rgba(226,75,74,0.32)";
}
function pillDot(p: PillState) {
  if (p === "go") return "#4dd4e8";
  if (p === "warn") return "#f0b23a";
  return "#e24b4a";
}
